import React from 'react'
import PropTypes from 'prop-types'

import cl from 'classnames'
import { Box, Hidden, Paper } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import theme from 'artkive/utils/theme'

import Bubbles from 'images/ecom/bubbles.svg'

const useStyles = makeStyles(({ breakpoints, spacing }) => ({
  container: {
    background: ({ productBg, descriptionBg }) =>
      productBg
        ? `linear-gradient(90deg, ${productBg} 54%, #F6F9FA 54%, #F6F9FA calc(54% + 32px), ${descriptionBg} calc(54% + 32px))`
        : 'unset',
    display: 'flex',
    [breakpoints.down('lg')]: {
      background: ({ productBg, descriptionBg }) =>
        productBg
          ? `linear-gradient(90deg, ${productBg} 50%, #F6F9FA 50%, #F6F9FA calc(50% + 32px), ${descriptionBg} calc(50% + 32px))`
          : 'unset',
    },
    [breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    '&$singleVariant': {
      height: '100%',
      [breakpoints.down('sm')]: {
        height: 'auto',
      },
    },
  },
  imageWrapper: {
    position: 'relative',
    backgroundColor: ({ productBg }) => productBg ?? 'unset',
    boxSizing: 'border-box',
    overflow: 'auto',
    display: 'flex',
    alignItems: 'center',
    minWidth: ({ single }) => (single ? '45%' : '54%'),
    [breakpoints.down('lg')]: {
      minWidth: ({ single }) => (single ? '45%' : '50%'),
    },
  },
  productImage: {
    width: '100%',
    height: '100%',
    position: 'relative',

    '&.contain': {
      paddingBottom: '66%',

      [breakpoints.down('sm')]: {
        paddingBottom: '75%',
      },

      '& img': {
        objectFit: 'contain',
      },
    },
    '&.cover': {
      paddingBottom: '74.87%',

      '& img': {
        objectFit: 'cover',
      },

      [breakpoints.up('md')]: {
        paddingBottom: '66.66%',
      },
    },
    '& img': {
      position: 'absolute',
      width: '100%',
      height: '100%',
    },
  },
  imagePadding: {
    width: '100%',
  },
  contentWrapper: {
    flexGrow: 1,
    marginLeft: 0,
    position: 'relative',
    '&$singleVariant': {
      marginLeft: 80,
      position: 'relative',
    },
    [breakpoints.down('md')]: {
      '&$singleVariant': {
        backgroundColor: 'unset',
        marginLeft: 0,
      },
    },
    [breakpoints.up('md')]: {
      position: 'relative',
    },
    [breakpoints.down('sm')]: {
      backgroundColor: 'unset',
    },
  },
  singleVariant: {},
  wrapper: {
    height: '100%',
    display: 'flex',
    zIndex: 2,
    '&$singleVariant': {
      height: 'auto',
      position: 'absolute',
      left: spacing(-8.5),
      top: '50%',
      maxWidth: '100%',
      transform: 'translate(0, -50%)',
      [breakpoints.down('md')]: {
        position: 'relative',
        left: 0,
        top: 0,
        transform: 'translate(0, 0)',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
    [breakpoints.up('xl')]: {
      height: 'auto',
      position: 'absolute',
      left: -120,
      top: '50%',
      maxWidth: '100%',
      transform: 'translate(0, -50%)',
    },
    [breakpoints.down('md')]: {
      position: 'relative',
      left: 0,
      top: 0,
      transform: 'translate(0, 0)',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  bubblesWrapper: {
    position: 'absolute',
    bottom: '69px',
    right: '29px',
    zIndex: 1,
  },
  content: {
    flex: 1,
    zIndex: 1,
    padding: '40px 100px',
    minWidth: 840,
    boxShadow: theme.shadows[5],
    [breakpoints.down('lg')]: {
      padding: '40px 60px',
      minWidth: 'unset',
      boxShadow: 'none',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    [breakpoints.down('md')]: {
      padding: '40px',
    },
    [breakpoints.down('sm')]: {
      height: 'auto',
      padding: '32px 16px',
    },
    '$singleVariant &': {
      padding: '32px 80px',
      [breakpoints.down('sm')]: {
        padding: '32px 16px',
      },
    },
  },
}))

const ProductDescriptionSection = ({
  children,
  image,
  video,
  single,
  productBg,
  descriptionBg,
  overImage,
  className,
  descriptionClassName,
  imageStyle = 'cover',
  gutter = true,
  withBubbles,
  classes: classesProp,
  ...rest
}) => {
  const classes = useStyles({ productBg, descriptionBg, classes: classesProp })

  return (
    <Box
      className={cl(classes.container, className, {
        [classes.singleVariant]: single,
      })}
      {...rest}
    >
      <Box className={classes.imageWrapper} single={single}>
        {video || <Box className={cl(classes.productImage, imageStyle)}>{image}</Box>}
        {overImage}
      </Box>
      <Box className={classes.contentWrapper}>
        <Box
          className={cl(classes.wrapper, {
            gutter,
          })}
        >
          <Paper elevation={0} className={cl(classes.content, descriptionClassName)}>
            <Box>{children}</Box>
          </Paper>
        </Box>
        {withBubbles && (
          <Hidden lgDown>
            <Box className={classes.bubblesWrapper}>
              <img src={Bubbles} width='122' />
            </Box>
          </Hidden>
        )}
      </Box>
    </Box>
  )
}

ProductDescriptionSection.propTypes = {
  children: PropTypes.node,
  image: PropTypes.node,
  video: PropTypes.node,
  single: PropTypes.bool,
  gutter: PropTypes.bool,
  productBg: PropTypes.string,
  className: PropTypes.string,
  descriptionClassName: PropTypes.string,
  descriptionBg: PropTypes.string,
  overImage: PropTypes.node,
  imageStyle: PropTypes.oneOf(['contain', 'cover']),
  withBubbles: PropTypes.bool,
  classes: PropTypes.shape({
    wrapper: PropTypes.string,
  }),
}

export default ProductDescriptionSection
